import React, { createContext, Reducer, useReducer } from 'react';
import { produce } from 'immer';

export type MfaState = {
	mfaInitialized: boolean;
	phoneFactor?: {
		id: string;
		value: string;
	}
	biometricsFactor?: {
		id: string;
		value: string;
	}
	recoveryCodeFactor?: {
		id: string;
		value: string;
	}
}

export type Actions = SetMfaFactors | RemoveMfaFactor |Initialized;

export type SetMfaFactors = {
	type: 'setMfaFactors';
	factors: RawMfaFactor[];
}

export type RemoveMfaFactor = {
	type: 'removeMfaFactor';
	factor: 'phone' | 'biometrics' | 'recoveryCode';
}

export type Initialized = {
	type: 'initialized';
}

export type RawMfaFactor = {
	id: string;
	name: string;
	authenticator_type: 'recovery-code';
	oob_channel?: 'sms' | 'webauthn-platform';
}

const Dispatch = createContext<React.Dispatch<Actions>>(() => {});
const State = createContext<MfaState>({
	mfaInitialized: false
});

const reducer: Reducer<MfaState, Actions> = (state, action) => {
	switch (action.type) {
		case 'initialized':
			return {
				...state,
				mfaInitialized: true
			};

		case 'setMfaFactors':

			return produce(state, (draft) => {
				for(const factor of action.factors) {
					if(factor.oob_channel === 'sms'){
						draft.phoneFactor = {
							id: factor.id,
							value: factor.name
						}
					}
					if(factor.oob_channel === 'webauthn-platform'){
						draft.biometricsFactor = {
							id: factor.id,
							value: factor.name
						}
					}
					if(factor.authenticator_type === 'recovery-code'){
						draft.recoveryCodeFactor = {
							id: factor.id,
							value: factor.name
						}
					}
				}
			});
		case 'removeMfaFactor':
			return produce(state, (draft) => {
				if(action.factor === 'phone') delete draft.phoneFactor
				if(action.factor === 'biometrics') delete draft.biometricsFactor
				if(action.factor === 'recoveryCode') delete draft.recoveryCodeFactor
			});
		default:
			throw new Error('Invalid action type in MFA reducer');
	}
}

function Provider({ children }: { children: JSX.Element[] | JSX.Element }) {
	const [state, dispatch] = useReducer(reducer, {
		mfaInitialized: false
	});

	// The state and the dispatch function should be provided separately so that components that are interested
	// only in dispatching events don't need to use the state and thus re-render when the state changes
	// See here - https://reactjs.org/docs/hooks-faq.html#how-to-avoid-passing-callbacks-down
	return (
		<State.Provider value={state}>
			<Dispatch.Provider value={dispatch}>{children}</Dispatch.Provider>
		</State.Provider>
	)
}

export const MfaFactors = {
	State,
	Dispatch,
	Provider
};